import cx from 'classnames';
import dateFormat from 'dateformat';
import _ from "lodash";

import UserIcon from "src/pages/dealRoom/components/chat/userIcon";
import { Message, UserDetails } from "../index";

import styles from "./styles.module.scss";
import { ReactElement } from 'react';

interface MessageComponentProps {
  message: Message;
  user: UserDetails;
  isSender: boolean;
  showOnlineStatus: boolean;
  dealRoomId: string;
}

/**
 * Builds the in-app URL for a serialized mention token `@[type:id:label]`.
 * - asset / assets → `/assets/{id}`
 * - user / contact / people → deal room people area
 * - otherwise → `/deal-room/{dealRoomId}/{type}`
 */
export const getMentionRedirectUrl = (
  dealRoomId: string,
  mentionType: string,
  entityId: string
): string => {
  const normalized = mentionType.toLowerCase();

  if (!dealRoomId) return "#";
  
  if (normalized === "asset" || normalized === "assets") {
    return `/assets/${entityId}`;
  }

  if (normalized === "user" || normalized === "contact" || normalized === "people") {
    return `/deal-room/${dealRoomId}/people/internal`;
  }

  if(normalized === "stage" || normalized === "action"){
    return `/deal-room/${dealRoomId}/action-plan`;
  }

  return `/deal-room/${dealRoomId}/${encodeURIComponent(mentionType)}`;
};

const parseMessageContent = (content: string, dealRoomId: string) => {
  const mentionRegex = /([@/])\[([^:]+):([^:]+):([^\]]+)\]/g;

  const elements: ReactElement[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      elements.push(<span key={`text-${lastIndex}`}>{content.slice(lastIndex, match.index)}</span>);
    }
    
    const [, char, type, id, label] = match;

    if (char === "@") {
      const href = getMentionRedirectUrl(dealRoomId, type, id);
      elements.push(
        <a
          key={`mention-${id}-${match.index}`}
          href={href}
          target={"_blank"}
          className={styles.mention}
        >
          {char}
          {label}
        </a>
      );
    } else if (char === "/") {
      elements.push(
        <span key={`command-${id}-${match.index}`} className={styles.mention}>
          {char}
          {label}
        </span>
      );
    }

    lastIndex = mentionRegex.lastIndex;
  }

  if (lastIndex < content.length) {
    elements.push(<span key={`text-${lastIndex}`}>{content.slice(lastIndex)}</span>);
  }

  return elements;
};

const MessageComponent: React.FC<MessageComponentProps> = ({ message, user, isSender, showOnlineStatus, dealRoomId }) => {
  const getMessageSentTime = () => {
    let timestamp = +Date();
    const receivedTime = _.get(message, 'receivedTime', null);
    
    if (receivedTime) {
      timestamp = receivedTime;
    }

    const result = dateFormat(timestamp / 10000, 'mmm d, yy hh:MM TT');

    return result;
  }

  return (
    <div className={cx(styles.messageSectionContainer, {
      [styles.receiver]: !isSender,
      [styles.sender]: isSender,
    })}>
      <div className={cx(styles.messageInfoContainer, {
        [styles.receiver]: !isSender,
        [styles.sender]: isSender,
      })}>
        <UserIcon showOnlineStatus={showOnlineStatus} isOnline={isSender} imageSrc={_.get(user, 'profilePicture', '')} />
        <p className={styles.username}>{`${_.get(user, 'name', '')}`}</p>
        <p className={styles.messageTime}>{getMessageSentTime()}</p>
      </div>
      {
        _.map(_.get(message, 'contents', []), ((content: string) => {
          return (
            <div className={cx(styles.message, {
              [styles.sent]: !isSender,
              [styles.received]: isSender,
            })} key={message.id}>{parseMessageContent(content, dealRoomId)}
            </div>
          );
        }))
      }
    </div>
  )
}

export default MessageComponent;